<template>
  <div class="review-pay-card">
    <!-- LEFT : recap -->
    <aside class="review-pay-card__col review-pay-card__col--recap">
      <div class="review-pay-card__eyebrow">
        {{ t('billing.review.recapEyebrow') }}
      </div>

      <div class="review-pay-card__header">
        <div class="review-pay-card__header-icon">
          <i :class="recapIcon || 'fa-regular fa-receipt'" />
        </div>
        <div class="review-pay-card__header-info">
          <div class="review-pay-card__header-title">
            {{ recapTitle }}
          </div>
          <div
            v-if="recapSubtitle"
            class="review-pay-card__header-subtitle"
          >
            {{ recapSubtitle }}
          </div>
        </div>
      </div>

      <div
        v-if="recapLines.length > 0"
        class="review-pay-card__lines"
      >
        <div
          v-for="(line, i) in recapLines"
          :key="i"
          class="review-pay-card__row"
          :class="`review-pay-card__row--${line.kind || 'normal'}`"
        >
          <span>{{ line.label }}</span>
          <span>{{ line.value }}</span>
        </div>
      </div>

      <!-- Caller-provided extra content (e.g. "Prochain renouvellement le …",
           a note, a badge). Rendered between the lines and the total. -->
      <slot name="recap-extra" />

      <div class="review-pay-card__total">
        <div class="review-pay-card__total-label">
          {{ totalLabel }}
        </div>
        <div class="review-pay-card__total-value">
          {{ totalValue }}
        </div>
      </div>
    </aside>

    <div class="review-pay-card__divider" />

    <!-- RIGHT : payment -->
    <section class="review-pay-card__col review-pay-card__col--pay">
      <div class="review-pay-card__eyebrow">
        {{ t('billing.review.payEyebrow') }}
      </div>

      <!-- `#payment-content` overrides the default PaymentBlock — used for
           cases where there's no Adyen flow (downgrade scheduled at next
           renewal, Shopify shop with external billing, etc.). -->
      <slot
        v-if="hasPaymentContentSlot"
        name="payment-content"
      />
      <PaymentBlock
        v-else
        :payment-mode="paymentMode"
        :payment-method-id="paymentMethodId"
        :default-card="defaultCard"
        :all-cards="allCards"
        :amount="amount"
        :currency="currency"
        :allowed-methods="allowedMethods"
        :submit-handler="dropinSubmitHandler"
        :details-handler="dropinDetailsHandler"
        :error-message="paymentError"
        :show-eyebrow="false"
        :disabled="disabled"
        @update:paymentMode="onPaymentModeChange"
        @update:paymentMethodId="onPaymentMethodIdChange"
        @success="onDropinSuccess"
        @error="onDropinError"
      />

      <!-- In-card CTA — shown when the parent provides a `ctaLabel` AND we're
           NOT in the new-card flow (the Adyen Drop-in has its own pay button).
           When `#payment-content` overrides the default, the CTA is always
           shown (downgrade / Shopify don't have a Drop-in). -->
      <button
        v-if="showCta"
        type="button"
        class="review-pay-card__cta"
        :disabled="ctaDisabled || ctaSubmitting"
        @click="onCtaClick"
      >
        <template v-if="!ctaSubmitting">
          <i
            class="review-pay-card__cta-icon"
            :class="ctaIcon || 'fa-regular fa-lock'"
          />
          <span class="review-pay-card__cta-label">{{ ctaLabel }}</span>
          <i class="fa-regular fa-arrow-right review-pay-card__cta-arrow" />
        </template>
        <template v-else>
          <i class="fa-regular fa-spinner-third fa-spin review-pay-card__cta-icon" />
          <span class="review-pay-card__cta-label">
            {{ ctaLoadingLabel || t('billing.review.processing') }}
          </span>
        </template>
      </button>

      <div
        v-if="trustText && showCta"
        class="review-pay-card__trust"
      >
        <i class="fa-regular fa-shield-halved" />
        <span>{{ trustText }}</span>
      </div>

      <!-- Caller-provided extra content (e.g. error message, secondary note). -->
      <slot name="payment-extra" />
    </section>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, useSlots,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PaymentBlock from '@/components/billing/shared/PaymentBlock.vue';
import { ActionResult } from '@/composables/billing/billingApi';
import { PaymentMethodItem } from '@/types/billing-types';

type PaymentMode = 'existing' | 'new';

export interface ReviewAndPayLine {
  label: string;
  value: string;
  /** Visual variant — drives a SCSS modifier on the row. */
  kind?: 'normal' | 'discount' | 'accent';
}

/**
 * Shared two-column « review + pay » card used by every billing checkout flow
 * (regularising an unpaid invoice, buying one-shot credits, changing plan,
 * etc.). The recap on the left is data-driven via props ; the payment column
 * delegates to {@link PaymentBlock} so the saved-card / new-card UX stays
 * consistent everywhere.
 *
 * The CTA is intentionally OUTSIDE this card — each parent dialog renders its
 * own footer with the right label / icon / handler. This keeps the card
 * itself stateless and reusable.
 */
export default defineComponent({
  name: 'ReviewAndPayCard',

  components: { PaymentBlock },

  props: {
    // ─── LEFT column (recap) ────────────────────────────────────────────
    recapTitle: {
      type: String,
      required: true,
    },

    recapSubtitle: {
      type: String,
      default: '',
    },

    /** FontAwesome class. Defaults to `fa-regular fa-receipt`. */
    recapIcon: {
      type: String,
      default: '',
    },

    recapLines: {
      type: Array as PropType<ReviewAndPayLine[]>,
      default: () => [],
    },

    totalLabel: {
      type: String,
      required: true,
    },

    /** Pre-formatted (e.g. `"11,99 €"`). The card is display-only. */
    totalValue: {
      type: String,
      required: true,
    },

    // ─── RIGHT column (payment) — forwarded to PaymentBlock ─────────────
    defaultCard: {
      type: Object as PropType<PaymentMethodItem | null>,
      default: null,
    },

    allCards: {
      type: Array as PropType<PaymentMethodItem[]>,
      default: () => [],
    },

    paymentMode: {
      type: String as PropType<PaymentMode>,
      default: 'existing',
    },

    paymentMethodId: {
      type: String as PropType<string | null>,
      default: null,
    },

    amount: {
      type: Number,
      required: true,
    },

    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },

    dropinSubmitHandler: {
      type: Function as PropType<(state: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    dropinDetailsHandler: {
      type: Function as PropType<(state: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    /**
     * Adyen payment methods exposed in the Drop-in when the user adds a new
     * method. Forwarded to {@link PaymentBlock}. See its docstring for the
     * recommended values per flow.
     */
    allowedMethods: {
      type: Array as PropType<string[]>,
      default: () => ['scheme'],
    },

    paymentError: {
      type: String as PropType<string | null>,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    // ─── In-card CTA ────────────────────────────────────────────────────
    /** When empty, no CTA is rendered. The CTA stays hidden when the user
     *  is in the new-card Drop-in flow (the Drop-in has its own pay button). */
    ctaLabel: {
      type: String,
      default: '',
    },

    ctaIcon: {
      type: String,
      default: '',
    },

    ctaSubmitting: {
      type: Boolean,
      default: false,
    },

    ctaDisabled: {
      type: Boolean,
      default: false,
    },

    /** Label shown next to the spinner while submitting. Defaults to
     *  `billing.review.processing`. */
    ctaLoadingLabel: {
      type: String,
      default: '',
    },

    /** Reassurance text under the CTA (e.g. « Paiement sécurisé »). When
     *  empty, no trust line is shown. */
    trustText: {
      type: String,
      default: '',
    },
  },

  emits: [
    'update:paymentMode',
    'update:paymentMethodId',
    'dropin-success',
    'dropin-error',
    'cta-click',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const slots = useSlots();

    const hasPaymentContentSlot = computed(() => !!slots['payment-content']);

    // CTA visibility :
    //   - parent passes a non-empty label
    //   - AND we're either showing a custom payment-content slot (downgrade /
    //     Shopify — those flows never go through the Drop-in) OR the user is
    //     using their saved card (mode='existing').
    // When mode='new', the Adyen Drop-in renders its own pay button — hiding
    // ours avoids a confusing double CTA.
    const showCta = computed(() => (
      !!props.ctaLabel
      && (hasPaymentContentSlot.value || props.paymentMode === 'existing')
    ));

    const onPaymentModeChange = (mode: PaymentMode) => emit('update:paymentMode', mode);
    const onPaymentMethodIdChange = (id: string | null) => emit('update:paymentMethodId', id);
    const onDropinSuccess = () => emit('dropin-success');
    const onDropinError = (err: string) => emit('dropin-error', err);
    const onCtaClick = () => emit('cta-click');

    return {
      t,
      hasPaymentContentSlot,
      showCta,
      onPaymentModeChange,
      onPaymentMethodIdChange,
      onDropinSuccess,
      onDropinError,
      onCtaClick,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.review-pay-card {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 24px -12px rgba(15, 23, 42, 0.08);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }

  &__col {
    padding: 22px 24px;
    display: flex;
    flex-direction: column;

    &--recap {
      background: linear-gradient(180deg, #f9fcf3 0%, #ffffff 60%);
    }

    &--pay {
      background: #ffffff;
    }
  }

  &__divider {
    background: #e2e8f0;

    @media (max-width: 720px) {
      height: 1px;
      width: 100%;
    }
  }

  &__eyebrow {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 14px;
  }

  // ─── Header (icon + title + subtitle) ────────────────────────────────
  &__header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px dashed #e2e8f0;
  }

  &__header-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: $brand-color-primary;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    flex-shrink: 0;
    box-shadow: 0 2px 8px -2px rgba(148, 200, 64, 0.45);
  }

  &__header-info {
    min-width: 0;
  }

  &__header-title {
    font-size: 1.0625rem;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }

  &__header-subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin-top: 3px;
  }

  // ─── Recap lines ──────────────────────────────────────────────────────
  &__lines {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 16px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 0.875rem;
    color: #475569;
    font-variant-numeric: tabular-nums;

    &--discount {
      color: #629d03;
      font-weight: 600;
    }

    &--accent {
      color: #0f172a;
      font-weight: 600;
    }
  }

  // ─── Total box ───────────────────────────────────────────────────────
  &__total {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
  }

  &__total-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #475569;
  }

  &__total-value {
    font-size: 1.625rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  // ─── In-card CTA + trust line ────────────────────────────────────────
  &__cta {
    appearance: none;
    border: none;
    background: $brand-color-primary;
    color: #fff;
    padding: 14px 22px;
    margin-top: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 14px;
    cursor: pointer;
    transition: all 160ms ease;
    box-shadow: 0 6px 16px -6px rgba(148, 200, 64, 0.55);
    line-height: 1;
    width: 100%;

    &:hover:not(:disabled) {
      background: #7aae26;
      transform: translateY(-1px);
      box-shadow: 0 10px 24px -6px rgba(148, 200, 64, 0.7);
    }
    &:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; }
  }

  &__cta-icon { font-size: 0.9375rem; }
  &__cta-label { letter-spacing: -0.01em; }
  &__cta-arrow {
    font-size: 0.8125rem;
    opacity: 0.85;
    margin-left: 2px;
  }

  &__trust {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 0.8125rem;
    color: #629d03;
    font-weight: 500;

    i { font-size: 0.875rem; }
  }
}
</style>
